import { Download, File, Network, PackageOpen } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../design-system/Button';
import type { AnalysisReport } from '../../engine/core/types';
import { analysisReportJson, analysisReportText } from '../../engine/reports/serializeReport';
import { outputFilename } from '../../engine/core/filenames';
import { downloadBlob } from '../../utilities/download';
import { formatBytes, formatDuration, shortHash } from '../../utilities/format';

export function FileDetails({ report }: { report: AnalysisReport }): React.JSX.Element {
  const [tab, setTab] = useState<'details' | 'package'>('details');
  const exportReport = (format: 'json' | 'txt'): void => {
    const source = format === 'json' ? analysisReportJson(report) : analysisReportText(report);
    downloadBlob(new Blob([source], { type: format === 'json' ? 'application/json' : 'text/plain' }), outputFilename(report.file.name, 'RECOVERY_REPORT', format));
  };
  return (
    <div className="details-panel">
      <div className="details-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'details'} onClick={() => setTab('details')}>Details</button>
        <button type="button" role="tab" aria-selected={tab === 'package'} onClick={() => setTab('package')}>Package</button>
      </div>
      {tab === 'details' ? (
        <>
          <section>
            <h2>File identity</h2>
            <dl>
              <div><dt>File name</dt><dd>{report.file.name}</dd></div>
              <div><dt>File type</dt><dd>{report.detected.format.toUpperCase()}</dd></div>
              <div><dt>Size</dt><dd>{formatBytes(report.file.size)}</dd></div>
              <div><dt>Modified</dt><dd>{new Date(report.file.lastModified).toLocaleString()}</dd></div>
              <div><dt>Duration</dt><dd>{formatDuration(report.durationMs)}</dd></div>
            </dl>
          </section>
          <section>
            <h2>Technical details</h2>
            <dl>
              <div><dt>Engine</dt><dd>{report.engineVersion}</dd></div>
              <div><dt>Ruleset</dt><dd>{report.rulesetVersion}</dd></div>
              <div><dt>{report.adapter === 'pdf' ? 'Objects' : 'Parts'}</dt><dd>{report.counts.packageParts}</dd></div>
              <div><dt>{report.adapter === 'pdf' ? 'JavaScript actions' : report.adapter === 'archive' || report.adapter === 'opendocument' ? 'Encrypted entries' : 'Macros'}</dt><dd>{report.adapter === 'pdf' ? `${report.counts.javascriptActions} · never executed` : report.adapter === 'archive' ? `${report.archive?.encryptedEntries ?? 0} · never decrypted` : report.adapter === 'opendocument' ? `${report.odt?.encryptedEntries ?? 0} · never decrypted` : report.hasMacros ? 'Detected · never executed' : 'Not detected'}</dd></div>
              <div><dt>Protection</dt><dd>{report.adapter === 'word'
                ? (report.documentProtected ? 'Word editing protection' : 'None detected')
                : report.adapter === 'powerpoint'
                  ? (report.presentationProtected ? 'Presentation modification protection' : 'None detected')
                  : report.adapter === 'pdf'
                    ? (report.pdf?.encrypted ? 'PDF encryption detected' : 'No encryption detected')
                    : report.adapter === 'archive'
                      ? (report.archive?.encryptedEntries ? 'Encrypted entries detected' : 'No entry encryption detected')
                      : report.adapter === 'opendocument'
                        ? (report.odt?.encryptedEntries ? 'ODF encryption detected' : 'No ODF encryption detected')
                  : (report.workbookProtected ? 'Workbook structure' : 'None at workbook level')}</dd></div>
              {report.adapter === 'word' ? <div><dt>Metadata fields</dt><dd>{Object.keys(report.word?.metadata ?? {}).length}</dd></div> : null}
              {report.adapter === 'powerpoint' ? <div><dt>Metadata fields</dt><dd>{Object.keys(report.powerPoint?.metadata ?? {}).length}</dd></div> : null}
              {report.adapter === 'pdf' ? <><div><dt>PDF version</dt><dd>{report.pdf?.version}</dd></div><div><dt>Metadata fields</dt><dd>{Object.keys(report.pdf?.metadata ?? {}).length}</dd></div></> : null}
              {report.adapter === 'archive' ? <><div><dt>Directory status</dt><dd>{report.archive?.directoryStatus}</dd></div><div><dt>Recoverable bytes</dt><dd>{formatBytes(report.archive?.recoveredBytes ?? 0)}</dd></div></> : null}
              {report.adapter === 'opendocument' ? <><div><dt>Mimetype</dt><dd>{report.odt?.mimetypeStatus}</dd></div><div><dt>Manifest</dt><dd>{report.odt?.manifestStatus}</dd></div><div><dt>Metadata fields</dt><dd>{Object.keys(report.odt?.metadata ?? {}).length}</dd></div></> : null}
            </dl>
          </section>
          <section>
            <h2>SHA-256</h2>
            <code className="hash">{shortHash(report.file.sha256)}</code>
          </section>
        </>
      ) : (
        <section>
          <h2>{report.adapter === 'pdf' ? 'PDF object inventory' : report.adapter === 'archive' ? 'ZIP entry inventory' : report.adapter === 'opendocument' ? 'ODT package inventory' : 'Package inventory'}</h2>
          <div className="package-summary"><span><PackageOpen />{report.parts.length} {report.adapter === 'pdf' ? 'objects' : report.adapter === 'archive' ? 'entries' : 'parts'}</span><span><Network />{report.adapter === 'pdf' ? `${report.pdf?.streams ?? 0} streams` : report.adapter === 'archive' ? `${report.archive?.intactEntries ?? 0} intact` : `${report.relationships.length} relationships`}</span></div>
          <ul className="package-mini-list">
            {report.parts.slice(0, 24).map((part) => <li key={part.name}><File /><code>{part.name}</code><span>{formatBytes(part.uncompressedSize)}</span></li>)}
          </ul>
          {report.parts.length > 24 ? <p className="muted">Showing 24 of {report.parts.length} {report.adapter === 'pdf' ? 'objects' : report.adapter === 'archive' ? 'entries' : 'parts'}.</p> : null}
        </section>
      )}
      <div className="details-panel__actions">
        <Button onClick={() => exportReport('json')}><Download />Export JSON</Button>
        <Button variant="quiet" onClick={() => exportReport('txt')}>TXT report</Button>
      </div>
    </div>
  );
}
